import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {CheckLivenessStatus, FrontendConfig} from '@npck/auth-frontend-api';
import {OauthRedirectErrorCode} from '@npck/errors-api';
import {AppConfigService, BusinessErrorCode, RouterService, timer} from '@npck/libs/commons';
import {SessionStorageService} from 'ngx-webstorage';
import {Subscription} from 'rxjs';
import {AuthHolderService} from '../../domain/auth-holder.service';
import {Error} from '../../infra/app-error-handler.service';
import {Path} from '../../path';

const TIMER_EXPIRATION_TIME = 10;

const ERRORS_WITH_DESCRIPTION = [
    OauthRedirectErrorCode.iin_not_found,
    OauthRedirectErrorCode.sms_limit_exceeded,
    OauthRedirectErrorCode.sms_code_attempts_exceeded,
    OauthRedirectErrorCode.liveness_attempts_exceeded,
    OauthRedirectErrorCode.server_error,
    OauthRedirectErrorCode.unauthorized,
    OauthRedirectErrorCode.esign_is_blocked,
    OauthRedirectErrorCode.esign_is_blocked_and_ready_to_reset,
    OauthRedirectErrorCode.gbdfl_photo_not_found,
    OauthRedirectErrorCode.gbdfl_person_under_18_years,
    OauthRedirectErrorCode.network_connectivity_error,
    OauthRedirectErrorCode.esign_error,
    OauthRedirectErrorCode.liveness_qr_expired,
    OauthRedirectErrorCode.liveness_error,
    BusinessErrorCode.GBDUL_ORGANIZATION_NOT_FOUND,
    BusinessErrorCode.GBDUL_ORGANIZATION_HAS_DIFFERENT_LEADER,
    BusinessErrorCode.EMPLOYEE_IS_NOT_IN_ORGANIZATION,
    BusinessErrorCode.DOCUMENT_NOT_FOUND_IN_TEST_DB,
    CheckLivenessStatus.LIMIT_PER_SESSION_EXCEEDED,
    Error.NETWORK_UNAVAILABLE,
    Error.CAMERA_UNAVAILABLE,
];

const ERRORS_WITH_BUTTON = [
    OauthRedirectErrorCode.gov_service_unavailable,
    OauthRedirectErrorCode.gbdfl_photo_not_found,
    OauthRedirectErrorCode.gbdfl_face_not_matched,
    OauthRedirectErrorCode.gbdfl_person_under_18_years,
    BusinessErrorCode.GBDUL_ORGANIZATION_NOT_FOUND,
    BusinessErrorCode.GBDUL_ORGANIZATION_HAS_DIFFERENT_LEADER,
    BusinessErrorCode.EMPLOYEE_IS_NOT_IN_ORGANIZATION,
    BusinessErrorCode.DOCUMENT_NOT_FOUND_IN_TEST_DB,
];

const ERRORS_WITH_CHECK_BUTTON = [
    BusinessErrorCode.GBDUL_ORGANIZATION_NOT_FOUND,
    BusinessErrorCode.GBDUL_ORGANIZATION_HAS_DIFFERENT_LEADER,
    BusinessErrorCode.EMPLOYEE_IS_NOT_IN_ORGANIZATION,
    BusinessErrorCode.DOCUMENT_NOT_FOUND_IN_TEST_DB,
];

const ERRORS_WITH_TIMER = [
    OauthRedirectErrorCode.iin_not_found,
    OauthRedirectErrorCode.sms_limit_exceeded,
    OauthRedirectErrorCode.sms_code_attempts_exceeded,
    OauthRedirectErrorCode.liveness_attempts_exceeded,
    OauthRedirectErrorCode.server_error,
    OauthRedirectErrorCode.unauthorized,
    OauthRedirectErrorCode.esign_is_blocked,
    OauthRedirectErrorCode.esign_error,
    OauthRedirectErrorCode.liveness_qr_expired,
    OauthRedirectErrorCode.liveness_error,
    OauthRedirectErrorCode.network_connectivity_error,
];

const ERROR_ICONS = new Map<string, string>([
    [OauthRedirectErrorCode.gov_service_unavailable, 'ic_gov_error'],
    [OauthRedirectErrorCode.gbdfl_photo_not_found, 'ic_gov_error'],
    [OauthRedirectErrorCode.gbdfl_face_not_matched, 'ic_gov_error'],
    [OauthRedirectErrorCode.gbdfl_person_under_18_years, 'ic_18_only'],
    [OauthRedirectErrorCode.sms_code_attempts_exceeded, 'ic_blocking'],
    [OauthRedirectErrorCode.liveness_attempts_exceeded, 'ic_blocking'],
    [OauthRedirectErrorCode.esign_is_blocked, 'ic_esign_is_blocked'],
    [OauthRedirectErrorCode.esign_is_blocked_and_ready_to_reset, 'ic_esign_is_blocked'],
    [OauthRedirectErrorCode.iin_not_found, 'ic_not_found'],
    [OauthRedirectErrorCode.esign_error, 'ic_esign_is_blocked'],
    [OauthRedirectErrorCode.liveness_qr_expired, 'ic_warning'],
    [OauthRedirectErrorCode.liveness_error, 'ic_liveness_error'],
    [BusinessErrorCode.GBDUL_ORGANIZATION_NOT_FOUND, 'ic_not_found'],
    [BusinessErrorCode.EMPLOYEE_IS_NOT_IN_ORGANIZATION, 'ic_warning'],
    [BusinessErrorCode.GBDUL_ORGANIZATION_HAS_DIFFERENT_LEADER, 'ic_warning'],
    [BusinessErrorCode.DOCUMENT_NOT_FOUND_IN_TEST_DB, 'ic_not_found'],
    [BusinessErrorCode.DOCUMENT_NOT_FOUND, 'ic_not_found'],
    [CheckLivenessStatus.LIMIT_PER_SESSION_EXCEEDED, 'ic_blocking'],
    [Error.NETWORK_UNAVAILABLE, 'ic_error_no_network'],
    [Error.CAMERA_UNAVAILABLE, 'ic_error_camera'],
    [Error.DEVICE_NOT_SUPPORTED, 'ic_warning_red'],
]);

const CAMERA_ERROR_SHOWN = 'CAMERA_ERROR_SHOWN';

const ERESIDENCY_FACE_NOT_MATCHED = 'eresidency_face_not_matched';

const KNOWN_ERROR_CODES = new Set<string>([
    ...Object.values(OauthRedirectErrorCode),
    ...Object.values(BusinessErrorCode),
    ...Object.values(CheckLivenessStatus),
    ...Object.values(Error),
]);

@Component({
    selector: 'auth-error',
    templateUrl: 'oauth-error.component.html',
    standalone: false
})
export class OauthErrorComponent implements OnInit, OnDestroy {
    @Input() error: any;
    @Input() requestId: string;
    @Input() buttonType: 'submit' | 'retry' | 'cameraInstructions';
    @Input() submitButtonText: string;
    @Input() showGoBackButton: boolean;

    @Output() close = new EventEmitter<void>();
    @Output() submit = new EventEmitter<void>();

    buttonText: string;
    errorTitle: string;
    errorSubtitle: string;
    errorIcon: string;
    time: number = TIMER_EXPIRATION_TIME;

    errorVariables: Record<string, Record<string, number>> = {};

    private subscription: Subscription;

    constructor(
        private routerService: RouterService,
        private sessionStorageService: SessionStorageService,
        private authHolderService: AuthHolderService,
    ) {
        this.errorVariables = {
            [OauthRedirectErrorCode.liveness_attempts_exceeded]: {
                count: authHolderService.blockTimeMinutes || 15,
            },
            [OauthRedirectErrorCode.sms_code_attempts_exceeded]: {
                count: authHolderService.blockTimeMinutes || 15,
            },
            [CheckLivenessStatus.LIMIT_PER_SESSION_EXCEEDED]: {
                count: authHolderService.blockTimeMinutes || 15,
            },
        };
    }

    ngOnInit(): void {
        if (!KNOWN_ERROR_CODES.has(this.error)) {
            this.error = OauthRedirectErrorCode.server_error;
        }
        this.errorIcon = ERROR_ICONS.get(this.error) || 'ic_error';
        this.errorTitle = 'i18n_lib_commonErrorTitle#' + this.error;
        if (ERRORS_WITH_DESCRIPTION.includes(this.error)) {
            this.errorSubtitle = 'i18n_lib_commonErrorSubtitle#' + this.error;
        }
        // e-Residency sessions compare the face against the e-Residency photo, so the GBD FL wording would mislead the user
        if (this.error === OauthRedirectErrorCode.gbdfl_face_not_matched && this.authHolderService.hasEresidencyScope) {
            this.errorTitle = 'i18n_lib_commonErrorTitle#' + ERESIDENCY_FACE_NOT_MATCHED;
            this.errorSubtitle = 'i18n_lib_commonErrorSubtitle#' + ERESIDENCY_FACE_NOT_MATCHED;
        }
        if (ERRORS_WITH_BUTTON.includes(this.error)) {
            this.buttonText = ERRORS_WITH_CHECK_BUTTON.includes(this.error) ? 'i18n_auth_buttonCheck' : 'i18n_auth_buttonBack';
        }
        this.showGoBackButton = this.showGoBackButton || ERRORS_WITH_TIMER.includes(this.error);
        if (ERRORS_WITH_TIMER.includes(this.error)) {
            this.subscription = timer(new Date().getTime(), TIMER_EXPIRATION_TIME)
                .subscribe({
                    next: time => this.time = time,
                    complete: () => this.close.emit(),
                });
        }
        if (this.error === Error.NETWORK_UNAVAILABLE) {
            this.buttonType = 'retry';
        }
        if (this.error === Error.CAMERA_UNAVAILABLE) {
            const cameraErrorShown = this.sessionStorageService.retrieve(CAMERA_ERROR_SHOWN) == true;
            if (cameraErrorShown) {
                this.sessionStorageService.clear(CAMERA_ERROR_SHOWN);
                this.routerService.navigate(Path.LIVENESS);
            } else {
                this.sessionStorageService.store(CAMERA_ERROR_SHOWN, true);
                this.buttonType = 'cameraInstructions';
            }
        }
    }

    ngOnDestroy(): void {
        this.subscription?.unsubscribe();
    }

    onRetry(): void {
        this.routerService.navigate(this.routerService.getPreviousUrl());
    }

    gotoCameraInstructions(): void {
        window.location.href = AppConfigService.config<FrontendConfig>().cameraInstructionsUrl;
    }

    reloadPage = (): void => window.location.reload();
}
